import { useEffect, useState } from "react";
import { Flame, Plus, Check, Loader2 } from "lucide-react";
import { calculatePhrase, createPhrase } from "@/lib/api";
import { toast } from "sonner";

/**
 * Inline "Add a phrase that = N" suggester shown when viewing a trending number.
 * As the user types, computes all cipher values; ciphers matching the target are
 * highlighted in gold. Saves to dictionary on click.
 */
export default function TrendingAddBanner({ targetValue, searchCount, onAdded }) {
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");
  const [breakdown, setBreakdown] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!text.trim()) {
      setBreakdown(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      calculatePhrase(text)
        .then((r) => setBreakdown(r.breakdown))
        .catch(() => setBreakdown(null));
    }, 220);
    return () => clearTimeout(timer);
  }, [text]);
  const matching = breakdown ? breakdown.filter((b) => b.value === targetValue) : [];
  const canSave = text.trim() && matching.length > 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
      await createPhrase({ text, tags: tagList, notes: `Decodes to ${targetValue}` });
      toast.success(`"${text}" added (matched in ${matching.length} cipher${matching.length === 1 ? "" : "s"})`);
      setText("");
      setTags("");
      setBreakdown(null);
      onAdded?.();
    } catch {
      toast.error("Failed to add phrase");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel border-[rgba(212,175,55,0.4)] p-5 md:p-6 bg-gradient-to-br from-[rgba(212,175,55,0.06)] to-transparent rise"
         data-testid="trending-banner">
      <div className="flex items-center gap-3 mb-3">
        <Flame size={18} className="text-[var(--primary)]" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] gold">
            Trending in your searches
          </div>
          <div className="font-display text-xl mt-0.5">
            You&apos;ve decoded <span className="gold">{targetValue}</span>{" "}
            <span className="muted text-sm">— {searchCount} times this week.</span>
          </div>
        </div>
      </div>
      <p className="muted text-xs leading-relaxed">
        Got a name or phrase you suspect decodes to {targetValue}? Type it below —
        ciphers matching <span className="gold">{targetValue}</span> light up live, and
        you can save it straight to the dictionary.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3">
        <input
          data-testid="trending-phrase-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`A phrase you think = ${targetValue}`}
          className="terminal-input px-4 py-3 text-base"
        />
        <input
          data-testid="trending-tags-input"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="tags (optional)"
          className="terminal-input px-4 py-3 text-sm"
        />
        <button
          data-testid="trending-add-btn"
          onClick={save}
          disabled={!canSave || saving}
          className="btn-primary px-5 py-3 text-xs flex items-center gap-2 disabled:opacity-40"
        >
          {(() => {
            if (saving) return <Loader2 className="animate-spin" size={14} />;
            return canSave ? <Check size={14} /> : <Plus size={14} />;
          })()}
          {canSave ? `Add (${matching.length} match)` : "Add"}
        </button>
      </div>

      {breakdown && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-wider muted mb-2">
            Live cipher preview — matches to {targetValue} highlighted
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="trending-cipher-preview">
            {breakdown.map((b) => {
              const hit = b.value === targetValue;
              return (
                <span
                  key={b.key}
                  className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${
                    hit
                      ? "border-[var(--primary)] text-[var(--primary)] bg-[rgba(212,175,55,0.12)]"
                      : "border-white/10 muted"
                  }`}
                >
                  {b.label}: <span className={hit ? "font-bold" : ""}>{b.value}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
